'use client';

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import Skeleton from 'react-loading-skeleton';
import { NotificationsListItem } from '@module/mdrt-org/shared/components/header/components/notifications/notifications-list/notifications-list-item';
import { useNotifications } from '@module/mdrt-org/shared/components/providers/notifications-context';
import { useUserContext } from '@module/mdrt-org/shared/components/providers/user-context';
import { type UserNotificationsDTO } from '@module/mdrt-org/shared/utils/data/get-user-notifications';
import { postUserNotificationStatusUpdate } from '@module/mdrt-org/shared/utils/data/post-user-notification-status-update';
import { AlertIcon } from '@shared/ui-components/svgs';
import NotificationsBellIcon from '@shared/ui-components/svgs/notifications-bell-icon';
import styles from './notifications-list.module.scss';
export const NotificationsList = () => {
  const $ = _c(23);
  const locale = useLocale();
  const {
    cmsContent,
    isError,
    isFetching,
    notifications,
    notificationsQueryKey,
    showMoreNotifications,
    totalNotificationsCount
  } = useNotifications();
  const {
    accessToken,
    personId
  } = useUserContext();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== accessToken || $[1] !== personId) {
    t0 = async t1 => {
      const {
        notificationId
      } = t1;
      return postUserNotificationStatusUpdate({
        accessToken,
        notificationId,
        personId
      });
    };
    $[0] = accessToken;
    $[1] = personId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["mark-notification-as-read"];
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  let t3;
  if ($[4] !== notificationsQueryKey || $[5] !== queryClient) {
    t2 = (_error, _variables, context) => {
      if (context?.previousNotificationsQueryData) {
        queryClient.setQueryData(notificationsQueryKey, context.previousNotificationsQueryData);
      }
    };
    t3 = async t4 => {
      const {
        notificationId: notificationId_0
      } = t4;
      await queryClient.cancelQueries({
        queryKey: notificationsQueryKey
      });
      const previousNotificationsQueryData = queryClient.getQueryData(notificationsQueryKey);
      queryClient.setQueryData(notificationsQueryKey, previousNotifications => {
        const wasUnread = previousNotifications?.notifications?.some(notification => notification.id === notificationId_0 && !notification.isRead);
        const previousUnreadCount = previousNotifications?.unreadCount ?? 0;
        return {
          ...previousNotifications,
          notifications: previousNotifications?.notifications?.map(notification_0 => {
            if (notification_0.id === notificationId_0) {
              return {
                ...notification_0,
                isRead: true
              };
            }
            return notification_0;
          }) ?? [],
          totalCount: previousNotifications?.totalCount ?? 0,
          unreadCount: wasUnread ? Math.max(previousUnreadCount - 1, 0) : previousUnreadCount
        };
      });
      return {
        previousNotificationsQueryData
      };
    };
    $[4] = notificationsQueryKey;
    $[5] = queryClient;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t0 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      mutationFn: t0,
      mutationKey: t1,
      onError: t2,
      onMutate: t3
    };
    $[8] = t0;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const {
    mutate
  } = useMutation(t4);
  if (isError) {
    let t5;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <AlertIcon className={styles.message__icon} />;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== cmsContent.errorMessage) {
      t6 = <div className={styles.message}>{t5}<div>{cmsContent.errorMessage}</div></div>;
      $[13] = cmsContent.errorMessage;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  let t5;
  if ($[15] !== cmsContent || $[16] !== isFetching || $[17] !== locale || $[18] !== mutate || $[19] !== notifications || $[20] !== showMoreNotifications || $[21] !== totalNotificationsCount) {
    t5 = notifications.length > 0 ? <div style={{
      position: "relative"
    }}><ul className={styles["notifications-list"]}>{notifications.map(notification_1 => <NotificationsListItem cmsContent={cmsContent} key={notification_1.id} locale={locale} mutate={mutate} notification={notification_1} />)}{isFetching && <li className={styles["notifications-list__item"]}><div className={styles["notifications-list__item-content"]}><div className={styles["notifications-list__item-message-container"]}><div style={{
                width: "100%"
              }}><div className={styles["notifications-list__item-title"]}><Skeleton height={16} width={180} /></div><p className={styles["notifications-list__item-message"]}><Skeleton count={4.7} height={14} width="100%" /></p><p className={styles["notifications-list__item-message-time"]}><Skeleton width={120} /></p></div></div></div></li>}</ul>{notifications.length < totalNotificationsCount && <button className={styles["show-more-button"]} onClick={showMoreNotifications} type="button">{cmsContent.showMoreButtonLabel}</button>}</div> : <div className={styles.message}><NotificationsBellIcon className={styles.message__icon} /><div>{cmsContent.noNotificationsMessage}</div></div>;
    $[15] = cmsContent;
    $[16] = isFetching;
    $[17] = locale;
    $[18] = mutate;
    $[19] = notifications;
    $[20] = showMoreNotifications;
    $[21] = totalNotificationsCount;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
};